import React, { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { z } from 'zod';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    sevenDayApi,
    useApproveBulkReservationsMutation,
    useCancelBulkReservationsMutation,
    useDeclineBulkReservationsMutation,
} from '../../../../../app/Slices/SevenDayApiSlice';
import toast from '../../../../shared/Toast';
import FormTextField from '../../../../shared/FormTextField';
import FailedTable from './FailedTable';
import { useDispatch } from 'react-redux';
import { BulkActionResponse } from '../../../../../app/Slices/SevenDayApiTypes';
import { ModalProps } from '../../../../types/modal';
import { CustomButton } from '../../../../../layout/styledcomponents/CustomButton';
import { theme } from '../../../../../shared/theme';

interface BulkActionModalProps extends ModalProps {
    ids: number[];
    action?: 'cancel' | 'decline' | 'approve';
}

const reasonOptions = [
    { label: 'Another Reservation', value: 'Another Reservation' },
    { label: 'Cleaning', value: 'Cleaning' },
    { label: 'Duplicate', value: 'Duplicate' },
    { label: 'Booking Cap Exceeded', value: 'Booking Cap Exceeded' },
    { label: 'Weather', value: 'Weather' },
    { label: 'Maintenance', value: 'Maintenance' },
    { label: 'Staff Unavailable', value: 'Staff Unavailable' },
    { label: 'Owners Request', value: 'Owners Request' },
    { label: 'Other', value: 'Other' },
] as const;

const CancelSchema = z.object({
    reason: z.enum(
        [
            'Another Reservation',
            'Cleaning',
            'Duplicate',
            'Booking Cap Exceeded',
            'Weather',
            'Maintenance',
            'Staff Unavailable',
            'Owners Request',
            'Other',
        ],
        { message: 'Required' },
    ),
    comment: z.string().optional(),
});

const DeclineSchema = CancelSchema.superRefine((val, ctx) => {
    if (
        val.reason === 'Other' &&
        (!val.comment || val.comment.trim().length < 3)
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Required',
            path: ['comment'],
        });
    }
});

const ApproveSchema = z.object({
    reason: z.string().optional(),
    comment: z.string().optional(),
});

const getCopy = (action?: BulkActionModalProps['action'], count?: number) => {
    const n = count ?? 0;
    const verb =
        action === 'approve'
            ? 'Approve'
            : action === 'decline'
              ? 'Decline'
              : 'Cancel';
    const noun = n === 1 ? 'reservation' : 'reservations';
    const title =
        action === 'approve'
            ? `Approve ${n} ${noun}?`
            : action === 'decline'
              ? `Decline ${n} ${noun}?`
              : `Cancel ${n} ${noun}?`;

    const subtitle =
        action === 'approve'
            ? 'This will approve all selected reservations.'
            : action === 'decline'
              ? 'This will decline all selected reservations.'
              : 'This will cancel all selected reservations.';

    const primaryLabel =
        action === 'approve'
            ? 'Approve reservations'
            : action === 'decline'
              ? 'Decline reservations'
              : 'Cancel reservations';
    const secondaryLabel = 'Abort';

    return { verb, noun, title, subtitle, primaryLabel, secondaryLabel };
};

const BulkActionModal = ({
    ids,
    action,
    open,
    onClose,
}: BulkActionModalProps) => {
    const dispatch = useDispatch();
    const isMobile = useMediaQuery('(max-width: 770px)');

    const [approveReservations, { isLoading: isApproving }] =
        useApproveBulkReservationsMutation();
    const [cancelReservations, { isLoading: isCancelling }] =
        useCancelBulkReservationsMutation();
    const [declineReservations, { isLoading: isDeclining }] =
        useDeclineBulkReservationsMutation();

    const isWorking = isApproving || isCancelling || isDeclining;

    const [response, setResponse] = useState<BulkActionResponse>();

    const Schema = useMemo(() => {
        if (action === 'cancel') return CancelSchema;
        if (action === 'decline') return DeclineSchema;
        return ApproveSchema;
    }, [action]);

    type Data = z.infer<typeof Schema>;

    const methods = useForm<Data>({
        defaultValues: { reason: '' as any, comment: '' },
        resolver: zodResolver(Schema),
        mode: 'onSubmit',
    });

    const { control, handleSubmit, reset } = methods;

    const reason = useWatch({ name: 'reason' as any, control });
    const count = ids?.length ?? 0;

    const { title, subtitle, primaryLabel, secondaryLabel } = useMemo(
        () => getCopy(action, count),
        [action, count],
    );

    const handleClose = () => {
        if (isWorking) return;
        onClose();
        reset();
        setResponse(undefined);
        dispatch(sevenDayApi.util.invalidateTags(['Reservations']));
    };

    const onSubmit = async (data: any) => {
        try {
            const request = { bookingIds: ids, ...data };

            let res: BulkActionResponse | undefined;
            if (action === 'cancel') {
                res = await cancelReservations(request).unwrap();
            } else if (action === 'decline') {
                res = await declineReservations(request).unwrap();
            } else if (action === 'approve') {
                res = await approveReservations(request).unwrap();
            }

            setResponse(res);
        } catch (error) {
            toast.error('Error performing bulk operation');
        }
    };

    const resultSummary = useMemo(() => {
        const succeeded = response?.succeeded?.length ?? 0;
        const failed = response?.failed?.length ?? 0;

        const past =
            action === 'approve'
                ? 'approved'
                : action === 'decline'
                  ? 'declined'
                  : 'cancelled';

        return { succeeded, failed, past };
    }, [response, action]);

    const showReason = action !== 'approve';
    const showComment = action === 'decline' && reason === 'Other';

    const canSubmit = useMemo(() => {
        if (action === 'approve') return true;
        if (!reason) return false;
        if (action === 'decline' && reason === 'Other') {
            const c = (methods.getValues('comment' as any) || '').trim();
            return c.length >= 3;
        }
        return true;
    }, [action, reason, methods]);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    {response ? (
                        <>
                            <DialogTitle sx={{ alignItems: 'flex-start' }}>
                                <Stack
                                    direction='row'
                                    spacing={1.25}
                                    alignItems='center'
                                >
                                    <WarningAmberRoundedIcon
                                        sx={{
                                            color: theme.palette.warning.main,
                                        }}
                                    />
                                    <Box>
                                        Results
                                        <Typography
                                            variant='body3'
                                            sx={{ opacity: 0.8 }}
                                        >
                                            Bulk action complete.
                                        </Typography>
                                    </Box>
                                </Stack>

                                <IconButton
                                    onClick={handleClose}
                                    disabled={isWorking}
                                    size='small'
                                    aria-label='close'
                                    sx={{ color: 'text.primary' }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </DialogTitle>

                            <DialogContent sx={{ py: 2 }}>
                                <Stack spacing={2}>
                                    {resultSummary.succeeded > 0 ? (
                                        <Alert
                                            severity='success'
                                            sx={{ borderRadius: 2 }}
                                        >
                                            <Typography
                                                variant='body3'
                                                sx={{ fontWeight: 800 }}
                                            >
                                                {`${resultSummary.succeeded} reservation${
                                                    resultSummary.succeeded ===
                                                    1
                                                        ? ''
                                                        : 's'
                                                } ${resultSummary.past} successfully.`}
                                            </Typography>
                                        </Alert>
                                    ) : null}

                                    {resultSummary.failed > 0 ? (
                                        <Box>
                                            <Typography
                                                sx={{ fontWeight: 900 }}
                                                gutterBottom
                                            >
                                                These reservations were not{' '}
                                                {resultSummary.past}:
                                            </Typography>
                                            <FailedTable
                                                data={response.failed}
                                            />
                                        </Box>
                                    ) : null}
                                </Stack>
                            </DialogContent>

                            <Divider />

                            <DialogActions sx={{ px: 2.5, py: 2 }}>
                                <Box
                                    sx={{
                                        width: '100%',
                                        display: 'flex',
                                        gap: 1,
                                        justifyContent: 'flex-end',
                                        flexWrap: isMobile ? 'wrap' : 'nowrap',
                                    }}
                                >
                                    <CustomButton
                                        variant='contained'
                                        onClick={handleClose}
                                        fullWidth={isMobile}
                                    >
                                        Close
                                    </CustomButton>
                                </Box>
                            </DialogActions>
                        </>
                    ) : (
                        <>
                            <DialogTitle sx={{ alignItems: 'flex-start' }}>
                                <Stack
                                    direction='row'
                                    spacing={1.25}
                                    alignItems='center'
                                >
                                    <WarningAmberRoundedIcon
                                        sx={{
                                            color: theme.palette.warning.main,
                                        }}
                                    />
                                    <Box>
                                        {title}
                                        <Typography
                                            variant='body3'
                                            sx={{ opacity: 0.8 }}
                                        >
                                            {subtitle}
                                        </Typography>
                                    </Box>
                                </Stack>

                                <IconButton
                                    onClick={handleClose}
                                    disabled={isWorking}
                                    size='small'
                                    aria-label='close'
                                    sx={{ color: 'text.primary' }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </DialogTitle>

                            <DialogContent sx={{ py: 2 }}>
                                <Stack spacing={2}>
                                    <Box
                                        sx={{
                                            borderRadius: 2,
                                            px: 1.5,
                                            py: 1.25,
                                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                            backgroundColor:
                                                theme.palette.custom
                                                    .cardActionBackground,
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 1.25,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                mt: '2px',
                                                width: 10,
                                                height: 10,
                                                borderRadius: '50%',
                                                backgroundColor:
                                                    theme.palette.warning.main,
                                                flexShrink: 0,
                                            }}
                                        />
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography
                                                variant='body3'
                                                sx={{ fontWeight: 900 }}
                                            >
                                                {action === 'approve'
                                                    ? `Approve ${count} reservation${count === 1 ? '' : 's'}`
                                                    : action === 'decline'
                                                      ? `Decline ${count} reservation${count === 1 ? '' : 's'}`
                                                      : `Cancel ${count} reservation${count === 1 ? '' : 's'}`}
                                            </Typography>

                                            {action === 'cancel' && (
                                                <Typography
                                                    variant='body3'
                                                    sx={{
                                                        mt: 0.25,
                                                        color: theme.palette
                                                            .text.secondary,
                                                    }}
                                                >
                                                    This action can’t be undone.
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>

                                    {showReason ? (
                                        <FormControl fullWidth size='small'>
                                            <InputLabel
                                                id='bulk-reason-label'
                                                sx={{
                                                    ...theme.typography.body1,
                                                    color: theme.palette.text
                                                        .secondary,
                                                    fontSize: '12px',
                                                    lineHeight: 1.2,
                                                    transform:
                                                        'translate(14px, 14px) scale(1)',
                                                    '&.MuiInputLabel-shrink': {
                                                        transform:
                                                            'translate(14px, -9px) scale(0.75)',
                                                    },
                                                }}
                                            >
                                                Select reason
                                            </InputLabel>
                                            <Select
                                                labelId='bulk-reason-label'
                                                value={(reason as any) || ''}
                                                label='Select reason'
                                                onChange={(e) => {
                                                    methods.setValue(
                                                        'reason' as any,
                                                        e.target.value as any,
                                                        {
                                                            shouldValidate: true,
                                                            shouldDirty: true,
                                                        },
                                                    );
                                                    if (
                                                        e.target.value !==
                                                        'Other'
                                                    ) {
                                                        methods.setValue(
                                                            'comment' as any,
                                                            '',
                                                            {
                                                                shouldValidate: true,
                                                                shouldDirty: true,
                                                            },
                                                        );
                                                    }
                                                }}
                                            >
                                                {reasonOptions.map((r) => (
                                                    <MenuItem
                                                        key={r.value}
                                                        value={r.value}
                                                    >
                                                        {r.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    ) : null}

                                    {showComment ? (
                                        <FormTextField
                                            name='comment'
                                            control={control}
                                            label='Tell us briefly'
                                            placeholder='e.g. Unexpected change of plans'
                                        />
                                    ) : null}
                                </Stack>
                            </DialogContent>

                            <Divider />

                            <DialogActions sx={{ px: 2.5, py: 2 }}>
                                <Box
                                    sx={{
                                        width: '100%',
                                        display: 'flex',
                                        gap: 1,
                                        justifyContent: 'space-between',
                                        flexWrap: isMobile ? 'wrap' : 'nowrap',
                                    }}
                                >
                                    <CustomButton
                                        variant='outlined'
                                        color='primary'
                                        onClick={handleClose}
                                        disabled={isWorking}
                                        fullWidth={isMobile}
                                    >
                                        {secondaryLabel}
                                    </CustomButton>

                                    <CustomButton
                                        loading={isWorking}
                                        variant='contained'
                                        type='submit'
                                        disabled={!canSubmit || isWorking}
                                        fullWidth={isMobile}
                                        color='warning'
                                    >
                                        {primaryLabel}
                                    </CustomButton>
                                </Box>
                            </DialogActions>
                        </>
                    )}
                </form>
            </FormProvider>
        </Dialog>
    );
};

export default BulkActionModal;
